import type { ModuleTypeEnum } from '../types/module-type'
import type { ViewConfig } from '../types/user-preferences'

export enum SavedQueryVisibilityEnum {
  PRIVATE = 'PRIVATE',
  ORGANIZATION = 'ORGANIZATION',
}

/**
 * A saved artifact in the `saved_queries` table.
 *
 * One row can carry a ScQL `query` string, a table `config` (filters +
 * columns + grouping), or BOTH — that is the whole point of the unification:
 * "this query + these columns + this grouping" is a single record with an
 * owner, a visibility and timestamps.
 *
 * - `query` non-empty, `config` null  -> a classic saved ScQL query
 * - `config` non-null                 -> a saved view (may also carry a query)
 */
interface SavedArtifactBase {
  id: string
  organizationId: string
  createdBy: string | null
  name: string
  description: string | null
  /** Module types this row targets (e.g. ['TASK']) */
  modules: ModuleTypeEnum[]
  /**
   * List surface a view belongs to. `null` is the module's top-level page;
   * embedded lists carry a {@link SAVED_VIEW_SURFACES} key or
   * `collection:<id>` so they keep their own bucket.
   */
  surface: string | null
  visibility: SavedQueryVisibilityEnum
  /**
   * The id this row had while it lived in the `UserPreference` views blob.
   * Lets the client dedupe a migrated view against the legacy payload that is
   * intentionally never deleted.
   */
  legacyViewId: string | null
  createdAt: string | Date
  updatedAt: string | Date
  /** Populated for list views */
  creator?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  } | null
}

/**
 * A saved ScQL query. `/saved-queries` only ever returns query-bearing rows,
 * so `query` stays non-nullable for every existing consumer.
 */
export interface SavedQuery extends SavedArtifactBase {
  query: string
  /** Table state (filters, columns, grouping). Null for query-only rows. */
  config: ViewConfig | null
}

export interface CreateSavedQueryInput {
  name: string
  description?: string
  query: string
  modules: ModuleTypeEnum[]
  visibility?: SavedQueryVisibilityEnum
}

export interface UpdateSavedQueryInput {
  name?: string
  description?: string | null
  query?: string
  modules?: ModuleTypeEnum[]
  visibility?: SavedQueryVisibilityEnum
}

/**
 * A saved view — the same row as {@link SavedQuery}, projected for the view
 * surfaces (ViewSelector). `config` is always present here.
 */
export interface SavedViewRecord extends SavedArtifactBase {
  /** ScQL carried alongside the view, when the org has that feature. */
  query: string | null
  config: ViewConfig
}

export interface CreateSavedViewInput {
  name: string
  description?: string | null
  moduleType: ModuleTypeEnum
  /** Omit / null for the module's top-level page. */
  surface?: string | null
  config: ViewConfig
  visibility?: SavedQueryVisibilityEnum
  /** Only accepted when the org has the ScQL saved-queries feature. */
  query?: string | null
  /** Set when promoting a legacy `UserPreference` view to a first-class row. */
  legacyViewId?: string | null
}

export interface UpdateSavedViewInput {
  name?: string
  description?: string | null
  config?: ViewConfig
  visibility?: SavedQueryVisibilityEnum
  query?: string | null
}
